import { useNavigate } from "@tanstack/react-router";
import { Award, Download, Plus, Users, X } from "lucide-react";
import { useState } from "react";
import { DataTable } from "@/components/shared/data-table";
import { DataToolbar } from "@/components/shared/data-toolbar";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { FilterRow } from "@/components/shared/filter-bar";
import { FilterPanel, selectedLabels } from "@/components/shared/filter-panel";
import { FilterToggleGroup } from "@/components/shared/filter-toggle-group";
import { interactiveRowProps } from "@/components/shared/interactive-row";
import { CursorPagination } from "@/components/shared/pagination";
import { SearchInput } from "@/components/shared/search-input";
import { ViewToggle } from "@/components/shared/view-toggle";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { EntityComboboxMulti } from "@/components/ui/entity-combobox";
import { useCursorList } from "@/hooks/use-cursor-list";
import { useEmploymentTypeOptions } from "@/hooks/use-employment-types";
import { useEntityOptionLabels } from "@/hooks/use-entity-options";
import { useExportPersons, usePersons } from "@/hooks/use-persons";
import { useRoleClassificationOptions } from "@/hooks/use-role-classifications";
import { useViewMode } from "@/hooks/use-view-mode";
import { useWorkLocationOptions } from "@/hooks/use-work-locations";
import { useSession } from "@/lib/auth-client";
import { carryListSearch } from "@/lib/list-search";
import { canEditAll } from "@/lib/permissions";
import { PERSON_STATUS_LABELS, PERSON_STATUSES } from "@/schemas/persons";
import { PersonAvatar } from "./PersonAvatar";
import { PersonCards } from "./PersonCards";
import { PersonCertificationBulkAssignDialog } from "./PersonCertificationBulkAssignDialog";
import { PersonFormDialog } from "./PersonFormDialog";
import { PersonEmploymentTypeBadge, PersonStatusBadge } from "./person-badges";

const LIMIT = 50;

// Derived from the schema's status source of truth so the filter can never
// drift from the form/enum (previously hardcoded — WI-016).
const STATUS_OPTIONS = PERSON_STATUSES.map((value) => ({
  value,
  label: PERSON_STATUS_LABELS[value],
}));

export function PersonsTable() {
  const navigate = useNavigate();
  // Location and role classification are admin-managed config. The FILTER
  // offers active values only — a retired one has no rows worth filtering to,
  // and it would sit in the dropdown forever. The BADGE below still resolves a
  // retired token through `labelFor`, so someone who holds one reads correctly.
  const locations = useWorkLocationOptions();
  const classifications = useRoleClassificationOptions();
  const { data: session } = useSession();
  const isAdmin = canEditAll(session?.user?.role);
  const exportPersons = useExportPersons();

  const {
    search,
    setSearch,
    debouncedSearch,
    sort,
    onSort,
    filters: filterState,
    setFilter,
    resetFilters,
    pageRequest,
    pagination,
    // Status defaults to "Active" (most lists care about current staff); every
    // other filter defaults to "All" (empty = no filter).
  } = useCursorList({
    sortKey: "name",
    defaultFilters: {
      statuses: ["active"] as string[],
      employmentTypes: [] as string[],
      locations: [] as string[],
      roleClassifications: [] as string[],
      departmentIds: [] as string[],
      roleTeamIds: [] as string[],
      skillIds: [] as string[],
      // "only" = show only people whose manager is inactive (WI-076 data-
      // quality drill-in from Org Health). A word, not "1", so the router's
      // search serializer keeps it verbatim in the URL.
      managerInactive: null as string | null,
    },
  });
  const {
    statuses: statusFilter,
    employmentTypes: employmentTypeFilter,
    locations: locationFilter,
    roleClassifications: roleClassificationFilter,
    departmentIds: departmentFilter,
    roleTeamIds: roleTeamFilter,
    skillIds: skillFilter,
    managerInactive: managerInactiveFilter,
  } = filterState;
  const [createOpen, setCreateOpen] = useState(false);

  // List vs card view. Persisted so the choice survives navigation/reload.
  const [view, setView] = useViewMode("persons.view");

  // ── Bulk selection (admin-only) ──
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);

  // Sort rides with the filters so the CSV export mirrors the on-screen order.
  const filters = {
    search: debouncedSearch,
    statuses: statusFilter,
    employmentTypes: employmentTypeFilter,
    locations: locationFilter,
    roleClassifications: roleClassificationFilter,
    departmentIds: departmentFilter,
    roleTeamIds: roleTeamFilter,
    skillIds: skillFilter,
    managerInactive: managerInactiveFilter === "only",
    sort: sort.key,
    dir: sort.dir,
  };

  const { data, isLoading, isError, error, refetch } = usePersons(filters, pageRequest);

  const rows = data?.data ?? [];

  // Clear selection whenever the visible set of rows could change (search,
  // filters, sort, or page) — carrying ids across pages is confusing since the
  // selected people are no longer on screen.
  const selectionResetKey = JSON.stringify({
    debouncedSearch,
    statusFilter,
    employmentTypeFilter,
    locationFilter,
    roleClassificationFilter,
    departmentFilter,
    roleTeamFilter,
    skillFilter,
    managerInactiveFilter,
    sort,
    cursor: pageRequest.cursor,
    direction: pageRequest.direction,
  });
  // React's "adjust state during render" pattern (preferred over a useEffect):
  // Filter chips come from the admin-managed employment-type list, so a type
  // added in Settings is filterable without a deploy (previously a constant
  // derived from a hard-coded union — EPIC-028).
  const employmentTypeOptions = useEmploymentTypeOptions().options;

  // when the key changes the selection is cleared before the next paint.
  const [prevResetKey, setPrevResetKey] = useState(selectionResetKey);
  if (prevResetKey !== selectionResetKey) {
    setPrevResetKey(selectionResetKey);
    setSelectedIds(new Set());
  }

  function toggleRow(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAllOnPage(checked: boolean) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const row of rows) {
        if (checked) next.add(row.id);
        else next.delete(row.id);
      }
      return next;
    });
  }

  function clearSelection() {
    setSelectedIds(new Set());
  }

  const selectedCount = selectedIds.size;

  // Resolve the entity filters' labels for the collapsed panel summary (the ids
  // alone aren't meaningful to show).
  const departmentLabels = useEntityOptionLabels("departments", departmentFilter);
  const roleTeamLabels = useEntityOptionLabels("role-teams", roleTeamFilter);
  const skillLabels = useEntityOptionLabels("skills", skillFilter);

  const activeFilterCount =
    statusFilter.length +
    employmentTypeFilter.length +
    locationFilter.length +
    roleClassificationFilter.length +
    departmentFilter.length +
    roleTeamFilter.length +
    skillFilter.length +
    (managerInactiveFilter === "only" ? 1 : 0);
  const hasActiveFilters = activeFilterCount > 0 || search.length > 0;

  // Reset restores the list's defaults (Status → Active), not an empty set.
  const clearFilters = resetFilters;

  // Join resolved entity labels for a collapsed summary chip (count fallback
  // while labels load).
  const joinLabels = (data: { label: string }[] | undefined, ids: string[]) =>
    data?.length ? data.map((o) => o.label).join(", ") : `${ids.length} selected`;

  // One summary chip per filter that has a selection, shown when the panel is
  // collapsed so the active filters stay visible. Classification values are
  // entity ids (no label on hand here), so it shows a count instead.
  const filterSummary: string[] = [];
  if (statusFilter.length)
    filterSummary.push(`Status: ${selectedLabels(STATUS_OPTIONS, statusFilter)}`);
  if (employmentTypeFilter.length)
    filterSummary.push(
      `Employment Type: ${selectedLabels(employmentTypeOptions, employmentTypeFilter)}`,
    );
  if (locationFilter.length)
    filterSummary.push(`Location: ${selectedLabels(locations.options, locationFilter)}`);
  if (roleClassificationFilter.length)
    filterSummary.push(
      `Role Classification: ${selectedLabels(classifications.options, roleClassificationFilter)}`,
    );
  if (departmentFilter.length)
    filterSummary.push(`Department: ${joinLabels(departmentLabels.data, departmentFilter)}`);
  if (roleTeamFilter.length)
    filterSummary.push(`Role Team: ${joinLabels(roleTeamLabels.data, roleTeamFilter)}`);
  if (skillFilter.length) filterSummary.push(`Skill: ${joinLabels(skillLabels.data, skillFilter)}`);
  if (managerInactiveFilter === "only") filterSummary.push("Inactive manager only");

  const controls = (
    <>
      <FilterPanel
        activeCount={activeFilterCount}
        summary={filterSummary}
        onReset={clearFilters}
        canReset={hasActiveFilters}
      >
        <FilterRow label="Status">
          <FilterToggleGroup
            options={STATUS_OPTIONS}
            value={statusFilter}
            onChange={(v) => setFilter("statuses", v)}
          />
        </FilterRow>
        <FilterRow label="Employment Type">
          <FilterToggleGroup
            options={employmentTypeOptions}
            value={employmentTypeFilter}
            onChange={(v) => setFilter("employmentTypes", v)}
          />
        </FilterRow>
        <FilterRow label="Location">
          <FilterToggleGroup
            options={locations.options}
            value={locationFilter}
            onChange={(v) => setFilter("locations", v)}
          />
        </FilterRow>
        <FilterRow label="Role Classification">
          <FilterToggleGroup
            options={classifications.options}
            value={roleClassificationFilter}
            onChange={(v) => setFilter("roleClassifications", v)}
          />
        </FilterRow>
        {/* Entity (server-searchable) filters, multi-select. Skill matches
            anyone whose active roles require any selected skill. */}
        <FilterRow label="Department">
          <EntityComboboxMulti
            entity="departments"
            className="w-full sm:w-72"
            value={departmentFilter}
            placeholder="All departments"
            onChange={(ids) => setFilter("departmentIds", ids)}
          />
        </FilterRow>
        <FilterRow label="Role Team">
          <EntityComboboxMulti
            entity="role-teams"
            className="w-full sm:w-72"
            value={roleTeamFilter}
            placeholder="All role teams"
            // Scope the option list to the selected department(s) — role teams
            // under any of them (empty = all departments).
            filters={
              departmentFilter.length ? { departmentId: departmentFilter.join(",") } : undefined
            }
            onChange={(ids) => setFilter("roleTeamIds", ids)}
          />
        </FilterRow>
        <FilterRow label="Skill">
          <EntityComboboxMulti
            entity="skills"
            className="w-full sm:w-72"
            value={skillFilter}
            placeholder="Any skill"
            filters={{ status: "active" }}
            onChange={(ids) => setFilter("skillIds", ids)}
          />
        </FilterRow>
        <FilterRow label="Reporting line">
          <div className="flex items-center gap-2 text-sm">
            <Checkbox
              id="persons-manager-inactive-filter"
              checked={managerInactiveFilter === "only"}
              onCheckedChange={(c) => setFilter("managerInactive", c === true ? "only" : null)}
            />
            <label htmlFor="persons-manager-inactive-filter">Inactive manager only</label>
          </div>
        </FilterRow>
      </FilterPanel>

      <DataToolbar
        search={
          <SearchInput
            value={search}
            onValueChange={setSearch}
            placeholder="Search by name, title, email..."
            className="flex-1 min-w-[200px] max-w-sm"
          />
        }
        actions={
          <>
            <ViewToggle value={view} onChange={setView} />
            {isAdmin && (
              <>
                <Button
                  variant="outline"
                  onClick={() => exportPersons.mutate(filters)}
                  disabled={exportPersons.isPending}
                >
                  <Download className="mr-2 h-4 w-4" />
                  {exportPersons.isPending ? "Exporting…" : "Export CSV"}
                </Button>
                <Button onClick={() => setCreateOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add person
                </Button>
              </>
            )}
          </>
        }
      />
    </>
  );

  if (isError) {
    return (
      <div className="space-y-4">
        {controls}
        <ErrorState message={error?.message ?? "Failed to load people"} onRetry={() => refetch()} />
        {isAdmin && <PersonFormDialog open={createOpen} onOpenChange={setCreateOpen} />}
      </div>
    );
  }

  // Shared between the table and card views so an empty result reads the same
  // regardless of layout.
  const emptyState = (
    <EmptyState
      icon={Users}
      title="No people found"
      description={
        hasActiveFilters
          ? "No people match your search and filters."
          : isAdmin
            ? "Add your first team member to get started."
            : "There are no people to show."
      }
      action={
        hasActiveFilters ? (
          <Button variant="outline" onClick={clearFilters}>
            Clear filters
          </Button>
        ) : isAdmin ? (
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add person
          </Button>
        ) : undefined
      }
    />
  );

  return (
    <div className="space-y-4">
      {controls}

      {isAdmin && view === "list" && selectedCount > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-muted/40 px-4 py-2.5">
          <span className="text-sm font-medium">{selectedCount} selected</span>
          <div className="ml-auto flex items-center gap-2">
            <Button size="sm" onClick={() => setBulkAssignOpen(true)}>
              <Award className="mr-2 h-4 w-4" />
              Assign certification
            </Button>
            <Button variant="ghost" size="sm" onClick={clearSelection}>
              <X className="mr-2 h-4 w-4" />
              Clear selection
            </Button>
          </div>
        </div>
      )}

      {view === "card" ? (
        <PersonCards persons={rows} loading={isLoading} empty={emptyState} />
      ) : (
        <DataTable
          selection={
            isAdmin
              ? {
                  pageIds: rows.map((r) => r.id),
                  selectedIds,
                  onToggleAll: toggleAllOnPage,
                }
              : undefined
          }
          headers={[
            { label: "Person", sortKey: "name" },
            { label: "Role", sortKey: "title", className: "hidden md:table-cell" },
            { label: "Team", sortKey: "team", className: "hidden lg:table-cell" },
            { label: "Department", sortKey: "department", className: "hidden xl:table-cell" },
            {
              label: "Role Classification",
              sortKey: "roleClassification",
              className: "hidden lg:table-cell",
            },
            {
              label: "Employment Type",
              sortKey: "employmentType",
              className: "hidden sm:table-cell",
            },
            { label: "Status", sortKey: "status" },
          ]}
          loading={isLoading}
          sort={sort}
          onSort={onSort}
          empty={emptyState}
        >
          {rows.map((person) => (
            <tr
              key={person.id}
              {...interactiveRowProps(
                () =>
                  navigate({
                    to: "/dashboard/persons/$personId",
                    params: { personId: person.id },
                    search: carryListSearch(),
                  }),
                `Open ${person.name}`,
              )}
            >
              {isAdmin && (
                // Stop click/keyboard events from bubbling to the row's navigate.
                <td
                  className="px-3 py-2.5"
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <Checkbox
                    aria-label={`Select ${person.name}`}
                    checked={selectedIds.has(person.id)}
                    onCheckedChange={() => toggleRow(person.id)}
                  />
                </td>
              )}
              <td className="px-4 py-2.5">
                <div className="flex items-center gap-2.5">
                  <PersonAvatar
                    name={person.name}
                    seed={person.id}
                    size="sm"
                    imageUrl={person.avatarUrl}
                  />
                  <div className="min-w-0 leading-tight">
                    <div className="truncate font-medium text-foreground">{person.name}</div>
                    {person.email && (
                      <div className="truncate text-xs text-muted-foreground">{person.email}</div>
                    )}
                  </div>
                </div>
              </td>
              <td className="hidden md:table-cell px-4 py-2.5 text-muted-foreground">
                {person.title || "—"}
              </td>
              <td className="hidden lg:table-cell px-4 py-2.5">
                {person.roleTeams.length > 0 ? (
                  <div className="flex flex-wrap items-center gap-1">
                    {person.roleTeams.slice(0, 2).map((team) => (
                      <span
                        key={team.id}
                        className="inline-flex items-center rounded-md border bg-muted/40 px-2 py-0.5 text-xs font-medium text-muted-foreground"
                      >
                        {team.name}
                      </span>
                    ))}
                    {person.roleTeams.length > 2 && (
                      <span className="text-xs text-muted-foreground">
                        +{person.roleTeams.length - 2}
                      </span>
                    )}
                  </div>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </td>
              <td className="hidden xl:table-cell px-4 py-2.5">
                {person.departmentName ? (
                  <span className="inline-flex items-center rounded-md border bg-muted/40 px-2 py-0.5 text-xs font-medium text-muted-foreground">
                    {person.departmentName}
                  </span>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </td>
              <td className="hidden lg:table-cell px-4 py-2.5">
                {person.roleClassification ? (
                  <span className="inline-flex items-center rounded-md border bg-muted/40 px-2 py-0.5 text-xs font-medium text-muted-foreground">
                    {classifications.labelFor(person.roleClassification)}
                  </span>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </td>
              <td className="hidden sm:table-cell px-4 py-2.5">
                <PersonEmploymentTypeBadge employmentType={person.employmentType} />
              </td>
              <td className="px-4 py-2.5">
                <PersonStatusBadge status={person.status} />
              </td>
            </tr>
          ))}
        </DataTable>
      )}

      <CursorPagination
        {...pagination(data)}
        limit={LIMIT}
        loading={isLoading}
        itemLabel="people"
      />

      {isAdmin && <PersonFormDialog open={createOpen} onOpenChange={setCreateOpen} />}
      {isAdmin && (
        <PersonCertificationBulkAssignDialog
          open={bulkAssignOpen}
          onOpenChange={setBulkAssignOpen}
          personIds={[...selectedIds]}
          onAssigned={clearSelection}
        />
      )}
    </div>
  );
}
